import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE = 25;

export interface TableQueryState {
  page: number;
  pageSize: number;
  sort: string | null;
  search: string;
  filters: Record<string, string[]>;
}

export interface UseTableQueryOptions {
  /** Filter param names this table understands. Anything else in the URL is ignored. */
  filterKeys: readonly string[];
  /**
   * Sort field names this table's backend accepts, required like `filterKeys`
   * rather than opt-in like `enableSegment` below.
   *
   * This was a real incident (MYN-127 Task 4 review, finding I-1 - the third
   * instance of this bug class, after `segment` and `published`): unlike an
   * unrecognized filter (which the URL/state layer here can simply not
   * apply), the Backend's sort utility THROWS `400 INVALID_SORT_FIELD` for
   * any `sort` value outside its own allowlist - it does not fall back or
   * ignore it. The Startups console's Published column was sortable and
   * produced `?sort=isPublished` URLs; once the Backend removed `isPublished`
   * from its sortable set, any bookmark or shared link carrying that sort
   * value 400'd the ENTIRE list, same blast radius as the `segment`/
   * `published` leaks. A column or field dropped from the Backend's sortable
   * set must be removed from this array in the same change - do that instead
   * of trusting every consumer to validate `sort` at its own call site.
   */
  sortKeys: readonly string[];
  defaultPageSize?: number;
  /**
   * Opts this console into `segment` (a UI-only tab value, never a
   * `filterKeys` entry - see `TableQueryState.filters`). Defaults to false:
   * a console that never calls `setSegment` must never read a stray
   * `?segment=` off the URL or emit it from `toQueryParams()` either.
   *
   * This was a real incident (MYN-127 Task 3 review): before this option
   * existed, `segment` was read off the URL and emitted unconditionally by
   * every consumer of this hook. The Users console's backend DTO accepts
   * `segment`, but the Startups console's does not and has
   * `forbidNonWhitelisted: true` - a hand-edited address bar, a stale
   * bookmark, or a shared link carrying a Users-console query string onto
   * `/admin/startups` made a stray `segment=` reject the ENTIRE request with
   * a 400, even though nothing in the Startups UI ever sets it. Gating
   * requires each future segment-shaped console (Investors/Partners, FR-52)
   * to explicitly opt in, rather than trusting every consumer to filter the
   * param at its own call site.
   */
  enableSegment?: boolean;
}

export interface UseTableQueryResult {
  state: TableQueryState;
  segment: string;
  selected: string | null;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  /** Clears search, segment, and every filter in ONE URL update. */
  clearFilters: () => void;
  /** Back to the console's default view: clears the sort as well as search, segment and filters, in ONE URL update. */
  resetView: () => void;
  setSort: (field: string) => void;
  setSearch: (value: string) => void;
  setFilter: (key: string, values: string[]) => void;
  /**
   * Set several filters in ONE URL update. Calling `setFilter` twice in one
   * tick keeps only the second (see `clearFilters`), so a control that moves
   * two filters at once, like a summary card, must use this.
   */
  setFilters: (patch: Record<string, string[]>) => void;
  setSegment: (value: string) => void;
  setSelected: (id: string | null) => void;
  /** Params to hand to the API client. Defaults and empties are omitted. */
  toQueryParams: () => Record<string, string>;
}

/** Parse a positive integer, falling back rather than yielding NaN or 0. */
function positiveInt(raw: string | null, fallback: number): number {
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : fallback;
}

/**
 * Drop a `sort` value whose field is not in the caller's allowlist, so a
 * stale/bookmarked/hand-edited URL can never reach the API with a sort field
 * the Backend has since removed. `raw` may carry a leading `-` for
 * descending; the allowlist check strips it, but the returned value (when
 * valid) keeps the original direction marker.
 */
function sanitizeSort(raw: string | null, sortKeys: readonly string[]): string | null {
  if (!raw) return null;
  const field = raw.startsWith('-') ? raw.slice(1) : raw;
  return sortKeys.includes(field) ? raw : null;
}

/**
 * Bind table state to the URL.
 *
 * The URL is the single source of truth, so a filtered view is shareable, the
 * back button behaves, and a refresh loses nothing. No table state is duplicated
 * in React state, which removes the class of bug where the two disagree.
 *
 * @param options Filter param names and optional page size.
 * @returns Current state plus setters. Every setter that changes what matches
 *          resets page to 1; `setSelected` deliberately does not, because opening
 *          a detail panel must not move the table underneath it.
 */
export function useTableQuery(
  options: UseTableQueryOptions,
): UseTableQueryResult {
  const { filterKeys, sortKeys, defaultPageSize = DEFAULT_PAGE_SIZE, enableSegment = false } = options;
  const [params, setParams] = useSearchParams();

  const state = useMemo<TableQueryState>(() => {
    const filters: Record<string, string[]> = {};

    for (const key of filterKeys) {
      const raw = params.get(key);
      if (raw) {
        const values = raw.split(',').filter(Boolean);
        if (values.length) filters[key] = values;
      }
    }

    return {
      page: positiveInt(params.get('page'), DEFAULT_PAGE),
      pageSize: positiveInt(params.get('pageSize'), defaultPageSize),
      sort: sanitizeSort(params.get('sort'), sortKeys),
      search: params.get('search') ?? '',
      filters,
    };
  }, [params, filterKeys, sortKeys, defaultPageSize]);

  // Not opted in -> always 'all', regardless of what a stray/bookmarked/
  // hand-edited URL happens to carry. `toQueryParams()` below only emits
  // `segment` when it is not 'all', so this single gate is also what keeps a
  // non-opted-in console from ever forwarding `segment` to its API.
  const segment = enableSegment ? (params.get('segment') ?? 'all') : 'all';
  const selected = params.get('selected');

  /**
   * Apply a patch to the URL.
   *
   * @param patch      Keys to set; null or '' removes the key.
   * @param resetPage  Whether this change invalidates the current page.
   */
  const update = useCallback(
    (patch: Record<string, string | null>, resetPage: boolean) => {
      const next = new URLSearchParams(params);

      for (const [key, value] of Object.entries(patch)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }

      // A narrowed result set can have fewer pages than the one we are on, which
      // would render an empty table with no explanation.
      if (resetPage) next.delete('page');

      setParams(next, { replace: false });
    },
    [params, setParams],
  );

  const setPage = useCallback(
    (page: number) => update({ page: String(page) }, false),
    [update],
  );

  // Resets the page: at 20 rows/page you might be on page 7, which does not
  // exist at 100 rows/page - keeping the offset would show an empty table.
  const setPageSize = useCallback(
    (size: number) => update({ pageSize: String(size) }, true),
    [update],
  );

  const setSort = useCallback(
    (field: string) => {
      const current = params.get('sort');
      const isSameFieldAscending = current === field;
      update({ sort: isSameFieldAscending ? `-${field}` : field }, true);
    },
    [params, update],
  );

  const setSearch = useCallback(
    (value: string) => update({ search: value }, true),
    [update],
  );

  const setFilter = useCallback(
    (key: string, values: string[]) =>
      update({ [key]: values.length ? values.join(',') : null }, true),
    [update],
  );

  const setFilters = useCallback(
    (patch: Record<string, string[]>) =>
      update(
        Object.fromEntries(
          Object.entries(patch).map(([key, values]) => [key, values.length ? values.join(',') : null]),
        ),
        true,
      ),
    [update],
  );

  const setSegment = useCallback(
    (value: string) => update({ segment: value === 'all' ? null : value }, true),
    [update],
  );

  const setSelected = useCallback(
    (id: string | null) => update({ selected: id }, false),
    [update],
  );

  /**
   * Clear every narrowing control at once.
   *
   * MUST be a single `update` call. Calling setSearch + setSegment + setFilter
   * in one tick does not work: each builds a fresh URLSearchParams from the same
   * `params` snapshot in its closure, so the last setParams wins and every
   * earlier clear is silently discarded.
   *
   * `selected` is deliberately left alone - clearing filters should not also
   * close an open detail panel.
   */
  const clearFilters = useCallback(() => {
    const patch: Record<string, string | null> = { search: null, segment: null };
    for (const key of filterKeys) patch[key] = null;
    update(patch, true);
  }, [filterKeys, update]);

  const resetView = useCallback(() => {
    const patch: Record<string, string | null> = { search: null, segment: null, sort: null };
    for (const key of filterKeys) patch[key] = null;
    update(patch, true);
  }, [filterKeys, update]);

  const toQueryParams = useCallback((): Record<string, string> => {
    const out: Record<string, string> = {
      page: String(state.page),
      pageSize: String(state.pageSize),
    };

    if (state.sort) out.sort = state.sort;
    if (state.search) out.search = state.search;
    if (segment !== 'all') out.segment = segment;

    for (const [key, values] of Object.entries(state.filters)) {
      out[key] = values.join(',');
    }

    return out;
  }, [state, segment]);

  return {
    state,
    segment,
    selected,
    setPage,
    setPageSize,
    clearFilters,
    resetView,
    setSort,
    setSearch,
    setFilter,
    setFilters,
    setSegment,
    setSelected,
    toQueryParams,
  };
}
